import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";

import { ProductProps } from "../../types/ComponentProps";

import CoinsIcon from "../../assets/img/navicons/coins-icon.png";

import Spinner from "../../components/spinner/Spinner";
import ProductCard from "../../components/productCard/ProductCard";

import ProductListContainerStyles from "./ProductListContainer.module.css";

import useGetUserPoint from "../../hooks/useGetUserPoint";
import useGetAllProducts from "../../hooks/useGetAllProducts";
import useGetProductByCategory from "../../hooks/useGetProductsByCategory";

const categories: { label: string; value: string }[] = [
  { label: "All", value: "All" },
  { label: "Apparel & Company Merch", value: "Apparel & Company Merch" },
  { label: "Drugstores", value: "Drugstores" },
  { label: "Food & Dining", value: "Food & Dining" },
  { label: "Malls & Groceries", value: "Malls & Groceries" },
  { label: "Retail & Shopping", value: "Retail & Shopping" },
  { label: "Sweets", value: "Sweets" },
  { label: "Travel & Experiences", value: "Travel & Experiences" },
  { label: "Wellness & Personal Care", value: "Wellness & Personal Care" },
];

interface ProductListContainerProps {
  showModalClickHandler: (productId: number, productType: string) => void;
}

const ProductListContainer: React.FC<ProductListContainerProps> = ({
  showModalClickHandler,
}) => {
  const { ref, inView } = useInView();

  const [input, setInput] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const {
    data: userPoints,
    error: userPointsError,
    isLoading,
  } = useGetUserPoint();
  const {
    getAllProductsData,
    isLoadingGetAllProducts,
    isErrorGetAllProducts,
    fetchNextPageGetAllProducts,
    hasNextPageGetAllProducts,
    isFetchingNextPageGetAllProducts,
  } = useGetAllProducts();
  const {
    getProductsByCategoryData,
    isLoadingGetProductsByCategory,
    isErrorGetProductsByCategory,
    fetchNextPageGetProductsByCategory,
    hasNextPageGetProductsByCategory,
    isFetchingNextPageGetProductsByCategory,
  } = useGetProductByCategory(activeCategory !== "All" ? activeCategory : "");

  useEffect(() => {
    if (!inView) return;

    if (activeCategory === "All") {
      if (hasNextPageGetAllProducts) {
        fetchNextPageGetAllProducts();
      }
    } else {
      if (hasNextPageGetProductsByCategory) {
        fetchNextPageGetProductsByCategory();
      }
    }
  }, [
    inView,
    activeCategory,
    hasNextPageGetAllProducts,
    fetchNextPageGetAllProducts,
    hasNextPageGetProductsByCategory,
    fetchNextPageGetProductsByCategory,
  ]);

  // Reset scroll when category changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeCategory]);

  // Product List
  const products = useMemo(() => {
    const pages =
      activeCategory === "All"
        ? getAllProductsData?.pages
        : getProductsByCategoryData?.pages;

    const flattened = pages?.flatMap((page: any) => page.products) || [];

    let list = [...flattened];

    if (input.trim()) {
      list = list.filter((product) =>
        product.productName.toLowerCase().includes(input.toLowerCase()),
      );
    }

    return list;
  }, [
    activeCategory,
    getAllProductsData?.pages,
    getProductsByCategoryData?.pages,
    input,
  ]);

  const isLoadingProducts =
    activeCategory === "All"
      ? isLoadingGetAllProducts
      : isLoadingGetProductsByCategory;

  const isErrorProducts =
    activeCategory === "All"
      ? isErrorGetAllProducts
      : isErrorGetProductsByCategory;

  return (
    <div className="main-container inside-page-container-blk">
      <div className={ProductListContainerStyles.shopPageInnerBlk}>
        <div>
          <div className={ProductListContainerStyles.shopPageLeftSectionBlk}>
            {/* Left Section - User Coins */}
            <div className={ProductListContainerStyles.shopPageCoinsSectionBlk}>
              <h2>Coins Balance</h2>
              <div
                className={ProductListContainerStyles.flexBoxBlk}
                style={{
                  alignItems: "center",
                }}
              >
                <img src={CoinsIcon} alt="Coins" />
                <div
                  style={{
                    marginTop: "14px",
                  }}
                >
                  {userPointsError ? (
                    <p className="error-text">Failed to load points</p>
                  ) : (
                    <>
                      <h1>
                        {isLoading ? (
                          <Spinner
                            variant="medium-spinner"
                            alignSpin="center-spinner"
                          />
                        ) : (
                          (userPoints?.points?.coins ?? 0)
                        )}
                      </h1>
                      <span>Coins</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Left Section - Filters */}
            <div
              className={
                ProductListContainerStyles.shopPageCategoriesSectionBlk
              }
            >
              <div className={ProductListContainerStyles.input}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z"
                    stroke="white"
                    stroke-width="1.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
              </div>

              <h2>Categories</h2>
              <div className={ProductListContainerStyles.categories}>
                {categories.map((category) => {
                  const isActive = category.value === activeCategory;
                  return (
                    <button
                      key={category.value}
                      onClick={() => setActiveCategory(category.value)}
                      style={{
                        background: isActive ? "#09172E" : "none",
                      }}
                    >
                      {category.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Products */}
        <div className={ProductListContainerStyles.shopPageRightSectionBlk}>
          <h1>Shop All</h1>
          <div
            className={ProductListContainerStyles.containerProductListShopBlk}
          >
            {isLoadingProducts ? (
              <Spinner variant="medium-spinner" alignSpin="center-spinner" />
            ) : isErrorProducts ? (
              <p className="error-text">Failed to load products</p>
            ) : (
              <div
                className={ProductListContainerStyles.containerBlkProductList}
              >
                {products.length > 0 ? (
                  products.map((product: ProductProps, idx) => (
                    <ProductCard
                      key={idx}
                      product={product}
                      showModalClickHandler={showModalClickHandler}
                    />
                  ))
                ) : (
                  <p>No Products Available.</p>
                )}
              </div>
            )}

            <div ref={ref} style={{ height: "1px" }}></div>
            {(activeCategory === "All"
              ? isFetchingNextPageGetAllProducts
              : isFetchingNextPageGetProductsByCategory) && (
              <div style={{ display: "block", padding: "20px 0px" }}>
                <Spinner variant="medium-spinner" alignSpin="center-spinner" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListContainer;
